import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Table, Button, Space, Modal, message, Card, theme, Tooltip, Tag, Input, Select, Row, Col } from 'antd';
import { EditOutlined, DeleteOutlined, PlusOutlined, HomeOutlined, StarOutlined, UserOutlined, EyeOutlined, FileTextOutlined, SearchOutlined, ClearOutlined } from '@ant-design/icons';
import moment from 'moment';
import { CONTRACT_API, FACTORY_API, TENANT_API } from '../constants/api';
import axiosInstance from '../services/axiosConfig';
import ContractForm from '../components/contracts/ContractForm';
import { useNavigate } from 'react-router-dom';
import ActionButtons from '../components/common/ActionButtons';

function Contracts() {
  const [data, setData] = useState({ items: [], total: 0, currentPage: 1, totalPages: 1 });
  const [loading, setLoading] = useState(false);
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [editingContract, setEditingContract] = useState(null);
  const [windowWidth, setWindowWidth] = useState(window.innerWidth);
  const [queryParams, setQueryParams] = useState({
    page: 1,
    limit: 10,
    search: '',
    status: undefined,
    tenantName: '',
    factoryName: ''
  });
  const [factories, setFactories] = useState([]);
  const [tenants, setTenants] = useState([]);
  const resizeTimeoutRef = useRef(null);
  const navigate = useNavigate();

  // Lấy danh sách nhà xưởng
  const fetchFactories = useCallback(async () => {
    try {
      const response = await axiosInstance.get(FACTORY_API.LIST);
      setFactories(response.data.factories || []);
    } catch (error) {
      message.error('Không thể tải danh sách nhà xưởng');
    }
  }, []);

  // Lấy danh sách khách thuê
  const fetchTenants = useCallback(async () => {
    try {
      const response = await axiosInstance.get(TENANT_API.ALL);
      setTenants(response.data.tenants || []);
    } catch (error) {
      message.error('Không thể tải danh sách khách thuê');
    }
  }, []);

  // Lấy danh sách hợp đồng
  const fetchContracts = useCallback(async () => {
    try {
      setLoading(true);
      const params = new URLSearchParams();
      Object.keys(queryParams).forEach(key => {
        if (queryParams[key] !== undefined && queryParams[key] !== '') {
          params.append(key, queryParams[key]);
        }
      });

      const response = await axiosInstance.get(`${CONTRACT_API.LIST}?${params.toString()}`);
      const contractsData = response.data.contracts.map(contract => ({
        ContractID: contract.ContractID,
        code: contract.ContractID,
        factoryId: contract.FactoryID,
        tenantId: contract.TenantID,
        signDate: contract.SignDate,
        startDate: contract.StartDate,
        endDate: contract.EndDate,
        duration: contract.Duration,
        RentAmount: contract.RentAmount,
        deposit: contract.Deposit,
        paymentTerm: contract.PaymentTerm,
        note: contract.Note || '',
        status: contract.Status.toLowerCase(),
        factory: contract.Factory,
        tenant: contract.Tenant,
        yearlyIncreaseRate: contract.YearlyIncreaseRate || 0,
        increaseRateDate: contract.IncreaseRateDate ? moment(contract.IncreaseRateDate).format('YYYY-MM-DD') : null,
        ServiceFee: contract.ServiceFee || 0,
        InfrastructureFee: contract.InfrastructureFee || 0,
        ContractFile: contract.ContractFile
      }));

      setData({
        items: contractsData,
        total: response.data.total,
        currentPage: response.data.currentPage,
        totalPages: response.data.totalPages
      });
    } catch (error) {
      message.error('Không thể tải danh sách hợp đồng');
    } finally {
      setLoading(false);
    }
  }, [queryParams]);

  useEffect(() => {
    fetchFactories();
    fetchTenants();
    fetchContracts();
  }, [fetchFactories, fetchTenants, fetchContracts]);

  const handleResize = useCallback(() => {
    if (resizeTimeoutRef.current) {
      clearTimeout(resizeTimeoutRef.current);
    }
    resizeTimeoutRef.current = setTimeout(() => {
      setWindowWidth(window.innerWidth);
    }, 200);
  }, []);

  useEffect(() => {
    window.addEventListener('resize', handleResize);
    return () => {
      if (resizeTimeoutRef.current) {
        clearTimeout(resizeTimeoutRef.current);
      }
      window.removeEventListener('resize', handleResize);
    };
  }, [handleResize]);

  const parseContractFiles = (contractFileStr) => {
    if (!contractFileStr) return [];

    try {
      if (Array.isArray(contractFileStr)) {
        return contractFileStr;
      }

      if (typeof contractFileStr === 'string') {
        if (contractFileStr.startsWith('http')) {
          return [contractFileStr];
        }

        try {
          const parsed = JSON.parse(contractFileStr);
          return Array.isArray(parsed) ? parsed : [parsed];
        } catch (error) {
          return [contractFileStr];
        }
      }

      return [];
    } catch (error) {
      console.error('Lỗi khi phân tích chuỗi file hợp đồng:', error);
      return [];
    }
  };

  const handleViewFile = (fileUrl) => {
    if (fileUrl.startsWith('http')) {
      window.open(fileUrl, '_blank');
    } else {
      const fullUrl = `${process.env.REACT_APP_API_URL}${fileUrl.startsWith('/') ? '' : '/'}${fileUrl}`;
      window.open(fullUrl, '_blank');
    }
  };

  const handleViewDetails = (contractId) => {
    navigate(`/contracts/${contractId}`);
  };

  const isMobile = windowWidth <= 768;
  const isTablet = windowWidth > 768 && windowWidth <= 1024;

  const formatCurrency = (value) => {
    if (!value && value !== 0) return '0 VNĐ';
    return value.toLocaleString('vi-VN') + ' VNĐ';
  };
  const getFileName = (filePath) => {
    if (!filePath) return '';
    const parts = filePath.split('\\');
    return parts[parts.length - 1];
  };
  const columns = [
    {
      title: 'Mã HĐ',
      dataIndex: 'code',
      key: 'code',
      width: isMobile ? 80 : 100,
      fixed: 'left',
    },

    {
      title: 'Khách thuê',
      dataIndex: 'tenant',
      key: 'tenant',
      width: isMobile ? 200 : 300,
      render: tenant => (
        <Tooltip
          title={
            <div>
              <div>{tenant?.TenantName}</div>
              <div>SĐT: {tenant?.Phone}</div>
              <div>Email: {tenant?.Email}</div>
            </div>
          }
        >
          <Tag icon={<UserOutlined />} color="orange" style={{ margin: 0 }}>
            {tenant?.TenantName || tenant?.TenantName}
          </Tag>
        </Tooltip>
      ),
      ellipsis: true,
    },
    {
      title: 'Nhà xưởng đang thuê',
      dataIndex: 'factory',
      key: 'factory',
      width: isMobile ? 120 : 200,
      render: factory => {
        return (
          <Tooltip
            title={
              <div>
                <div>{factory?.FactoryName || 'N/A'} {factory?.Area ? `- ${factory.Area}m²` : ''}</div>
                <div>Giá thuê: {factory?.Price ? `${factory.Price} VNĐ` : 'N/A'}</div>
              </div>
            }
          >
            <Tag icon={<HomeOutlined />} color="blue" style={{ margin: 0 }}>
              {factory?.FactoryName || 'N/A'}
            </Tag>
          </Tooltip>
        );
      },
    },
    {
      title: 'Ngày ký',
      dataIndex: 'signDate',
      key: 'signDate',
      width: isMobile ? 100 : 120,
      render: date => moment(date).format('DD/MM/YYYY'),
    },
    {
      title: 'Ngày bắt đầu',
      dataIndex: 'startDate',
      key: 'startDate',
      width: isMobile ? 100 : 120,
      render: date => moment(date).format('DD/MM/YYYY'),
    },
    {
      title: 'Ngày kết thúc',
      dataIndex: 'endDate',
      key: 'endDate',
      width: isMobile ? 100 : 120,
      render: date => moment(date).format('DD/MM/YYYY'),
    },
    {
      title: 'Thời hạn (tháng)',
      dataIndex: 'duration',
      key: 'duration',
      width: isMobile ? 100 : 120,
      align: 'right',
    },
    {
      title: 'Phí thuê/tháng',
      dataIndex: 'RentAmount',
      key: 'RentAmount',
      width: isMobile ? 120 : 150,
      align: 'right',
      render: value => formatCurrency(value),
    },
    {
      title: 'Phí dịch vụ/tháng',
      dataIndex: 'ServiceFee',
      key: 'ServiceFee',
      width: isMobile ? 120 : 150,
      align: 'right',
      render: value => formatCurrency(value),
    },
    {
      title: 'Phí hạ tầng/tháng',
      dataIndex: 'InfrastructureFee',
      key: 'InfrastructureFee',
      width: isMobile ? 120 : 150,
      align: 'right',
      render: value => formatCurrency(value),
    },
    {
      title: 'Tiền đặt cọc',
      dataIndex: 'deposit',
      key: 'deposit',
      width: isMobile ? 120 : 150,
      align: 'right',
      render: value => formatCurrency(value),
    },
    {
      title: 'Kỳ thanh toán',
      dataIndex: 'paymentTerm',
      key: 'paymentTerm',
      width: isMobile ? 120 : 150,
    },
    {
      title: 'Tăng giá (%/năm)',
      dataIndex: 'yearlyIncreaseRate',
      key: 'yearlyIncreaseRate',
      width: isMobile ? 100 : 150,
    },
    {
      title: 'Ghi chú',
      dataIndex: 'note',
      key: 'note',
      width: isMobile ? 150 : 150,
      ellipsis: true,
      render: note => note || 'N/A',
    },
    {
      title: 'File hợp đồng',
      dataIndex: 'ContractFile',
      key: 'ContractFile',
      width: isMobile ? 120 : 150,
      render: (contractFile, record) => {
        const files = record.ContractFile ? parseContractFiles(record.ContractFile) : [];

        if (files.length === 0) {
          return 'Chưa có file';
        }

        if (files.length === 1) {
          return (
            <Button
              icon={<EyeOutlined />}
              onClick={() => handleViewFile(files[0])}
              size="small"
            >
              Xem
            </Button>
          );
        }

        return (
          <Tooltip title={`${files.length} file đính kèm`}>
            <Button
              icon={<FileTextOutlined />}
              onClick={() => {
                Modal.info({
                  title: 'Danh sách file hợp đồng',
                  content: (
                    <Space direction="vertical" style={{ width: '100%' }}>
                      {files.map((file, index) => (
                        <Button
                          key={index}
                          type="link"
                          icon={<FileTextOutlined />}
                          onClick={() => handleViewFile(file)}
                          style={{ textAlign: 'left' }}
                        >
                          {getFileName(file)}
                        </Button>
                      ))}
                    </Space>
                  ),
                  width: 400,
                });
              }}
              size="small"
            >
              {files.length} File
            </Button>
          </Tooltip>
        );
      },
    },
    {
      title: 'Trạng thái',
      dataIndex: 'status',
      key: 'status',
      width: isMobile ? 100 : 150,
      render: status => (
        <Tag icon={<StarOutlined />} color={status === 'active' ? 'green' : status === 'expiring' ? 'orange' : status === 'expired' ? 'red' : 'blue'} style={{ margin: 0 }}>
          {status === 'active' ? 'Đang hiệu lực' : status === 'expiring' ? 'Sắp hết hạn' : status === 'expired' ? 'Đã hết hạn' : 'Đã thanh lý'}
        </Tag>
      ),
    },

    {
      title: 'Thao tác',
      key: 'action',
      fixed: 'right',
      width: isMobile ? 120 : 150,
      align: 'center',
      render: (_, record) => (
        <Space size={isMobile ? 4 : 8} style={{ display: 'flex', justifyContent: 'center' }}>
          <ActionButtons
            module="contracts"
            showAdd={false}
            onView={() => handleViewDetails(record.ContractID)}
            onEdit={() => showModal(record)}
            onDelete={() => handleDelete(record)}
            record={record}
          />
        </Space>
      ),
    },
  ];

  const showModal = async (record = null) => {
    setEditingContract(record);
    if (record) {
      console.log(record);
      console.log(record.ContractID);
      try {
        const response = await axiosInstance.get(CONTRACT_API.DETAIL(record.ContractID));
        setEditingContract(response.data);
      } catch (error) {
        message.error('Không thể tải thông tin hợp đồng');
      }
    }
    setIsModalVisible(true);
  };

  const handleDelete = (record) => {
    Modal.confirm({
      title: 'Xác nhận xóa',
      content: `Bạn có chắc chắn muốn xóa hợp đồng ${record.code}?`,
      okText: 'Xóa',
      okType: 'danger',
      cancelText: 'Hủy',
      onOk: async () => {
        try {
          await axiosInstance.delete(CONTRACT_API.DELETE(record.ContractID));
          message.success('Xóa hợp đồng thành công!');
          fetchContracts();
        } catch (error) {
          message.error('Có lỗi xảy ra khi xóa hợp đồng');
        }
      },
    });
  };

  const handleSubmit = async (values) => {
    try {
      if (editingContract) {
        await axiosInstance.put(CONTRACT_API.UPDATE(editingContract.ContractID), values);
        message.success('Cập nhật hợp đồng thành công!');
      } else {
        await axiosInstance.post(CONTRACT_API.CREATE, values);
        message.success('Thêm hợp đồng thành công!');
      }
      setIsModalVisible(false);
      fetchContracts();
    } catch (error) {
      message.error('Có lỗi xảy ra: ' + (error.response?.data?.message || error.message));
    }
  };

  const handleSearch = () => {
    setQueryParams(prev => ({
      ...prev,
      page: 1
    }));
  };

  const handleClearFilters = () => {
    setQueryParams({
      page: 1,
      limit: 10,
      search: '',
      status: undefined,
      tenantName: '',
      factoryName: ''
    });
  };

  return (
    <div style={{
      padding: '16px',
      backgroundColor: theme.useToken().colorBgContainer
    }}>
      <Card
        title={<h2 style={{ margin: 0 }}>Quản Lý Hợp Đồng</h2>}
        extra={
          <Space>
            <Button
              type="primary"
              icon={<PlusOutlined />}
              onClick={() => showModal()}
              size={isMobile ? "small" : "middle"}
            >
              {!isMobile && "Tạo hợp đồng mới"}
            </Button>
            <Button
              icon={<ClearOutlined />}
              onClick={handleClearFilters}
              size={isMobile ? "small" : "middle"}
              style={{backgroundColor: 'red',color: 'white',borderRadius: 5}}
            >
              {!isMobile && "Xóa Bộ Lọc"}
            </Button>
          </Space>
        }
        bodyStyle={{ padding: isMobile ? 8 : 16 }}
        bordered={false}
      >
        <Card
          size="small"
          style={{ marginBottom: 16 }}
          bodyStyle={{ padding: isMobile ? 8 : 16 }}
        >
          <Row gutter={[16, 8]} align="middle">
            <Col xs={24} sm={12} md={6} lg={6}>
              <Input
                placeholder="Tìm kiếm theo mã HĐ..."
                value={queryParams.search}
                onChange={(e) => setQueryParams(prev => ({ ...prev, search: e.target.value }))}
                onPressEnter={handleSearch}
                prefix={<SearchOutlined />}
                size={isMobile ? "small" : "middle"}
              />
            </Col>
            <Col xs={24} sm={12} md={6} lg={6}>
              <Input
                placeholder="Tìm theo tên khách thuê..."
                value={queryParams.tenantName}
                onChange={(e) => setQueryParams(prev => ({ ...prev, tenantName: e.target.value }))}
                onPressEnter={handleSearch}
                prefix={<UserOutlined />}
                size={isMobile ? "small" : "middle"}
              />
            </Col>
            <Col xs={24} sm={12} md={6} lg={6}>
              <Input
                placeholder="Tìm theo tên nhà xưởng..."
                value={queryParams.factoryName}
                onChange={(e) => setQueryParams(prev => ({ ...prev, factoryName: e.target.value }))}
                onPressEnter={handleSearch}
                prefix={<HomeOutlined />}
                size={isMobile ? "small" : "middle"}
              />
            </Col>
            <Col xs={24} sm={12} md={4} lg={4}>
              <Select
                placeholder="Trạng thái"
                value={queryParams.status}
                onChange={(value) => setQueryParams(prev => ({ ...prev, status: value }))}
                allowClear
                size={isMobile ? "small" : "middle"}
                style={{ width: '100%' }}
              >
                <Select.Option value="active">Đang hiệu lực</Select.Option>
                <Select.Option value="expiring">Sắp hết hạn</Select.Option>
                <Select.Option value="expired">Đã hết hạn</Select.Option>
                <Select.Option value="liquidated">Đã thanh lý</Select.Option>
              </Select>
            </Col>

          </Row>
        </Card>

        <Table
          columns={columns}
          dataSource={data.items}
          rowKey="id"
          scroll={{
            x: isMobile ? 980 : 1200
          }}
          pagination={{
            current: data.currentPage,
            pageSize: queryParams.limit,
            total: data.total,
            showSizeChanger: true,
            showQuickJumper: !isMobile,
            showTotal: (total) => `Tổng số ${total} bản ghi`,
            onChange: (page, pageSize) => {
              setQueryParams(prev => ({
                ...prev,
                page: page,
                limit: pageSize
              }));
            }
          }}
          loading={loading}
          size={isMobile ? "small" : "middle"}
        />
      </Card>
      <ContractForm
        visible={isModalVisible}
        onCancel={() => setIsModalVisible(false)}
        onSubmit={handleSubmit}
        initialValues={editingContract}
        title={editingContract ? 'Cập nhật hợp đồng' : 'Thêm hợp đồng mới'}
        factories={factories}
        tenants={tenants}
      />
    </div>
  );
}

export default Contracts; 